import { createSelector } from "reselect";
import {
  PROJECT_TEMPLATE_DISCUSSION,
  PROJECT_TEMPLATE_FILE,
  PROJECT_TEMPLATE_NOTE,
  PROJECT_TEMPLATE_RECURRING_TASK,
  PROJECT_TEMPLATE_SUBTASK,
  PROJECT_TEMPLATE_TASK,
  PROJECT_TEMPLATE_TASK_LIST,
} from "~/pages/ProjectTemplates/utils";
import { sortCompare } from "~/utils/utils";

export const getAllProjectTemplateElements = (state) => state.project_template_elements;

export const getProjectTemplateElementById = (state, id) => state.project_template_elements[id];

export const makeGetTemplateIdByProjectTemplateElementIdSelector = () => {
  return createSelector(getProjectTemplateElementById, (projectTemplateElement) => projectTemplateElement?.template_id);
};

export const makeGetTaskListIdByProjectTemplateElementIdSelector = () => {
  return createSelector(getProjectTemplateElementById, (projectTemplateElement) => {
    if (projectTemplateElement && projectTemplateElement.task_list_id) {
      return projectTemplateElement.task_list_id;
    }
  });
};

export const makeGetTaskListsByTemplateId = () => {
  return createSelector(
    getAllProjectTemplateElements,
    (state, templateId) => templateId,
    (projectTemplateElements, templateId) => {
      return Object.values(projectTemplateElements).filter(
        (element) => element.class === PROJECT_TEMPLATE_TASK_LIST && +element.template_id === +templateId
      );
    }
  );
};

export const makeGetSortedTaskListsByTemplateId = () => {
  return createSelector(makeGetTaskListsByTemplateId(), (taskLists) => {
    return taskLists.sort((a, b) => a.position - b.position);
  });
};

export const makeGetTemplateHasTasks = () => {
  return createSelector(
    getAllProjectTemplateElements,
    (state, templateId) => templateId,
    (projectTemplateElements, templateId) => {
      return (
        Object.values(projectTemplateElements).filter(
          (element) => element.class === PROJECT_TEMPLATE_TASK && +element.template_id === +templateId
        ).length > 1
      );
    }
  );
};

export const makeGetTaskListsByTemplateIdSortedByPosition = () => {
  return createSelector(makeGetTaskListsByTemplateId(), (projectTemplateTaskLists) => {
    return projectTemplateTaskLists.sort((a, b) => sortCompare(a.position, b.position));
  });
};

export const makeGetTasksByTaskListId = () => {
  return createSelector(
    getAllProjectTemplateElements,
    (state, taskListId) => taskListId,
    (projectTemplateElements, taskListId) => {
      return Object.values(projectTemplateElements).filter(
        (element) => element.class === PROJECT_TEMPLATE_TASK && +element.task_list_id === +taskListId
      );
    }
  );
};

export const makeGetSortedTasksByTaskListId = () => {
  return createSelector(makeGetTasksByTaskListId(), (tasks) => {
    return tasks.sort((a, b) => a.position - b.position);
  });
};

export const makeGetSortedTaskIdsByTaskListId = () => {
  return createSelector(makeGetSortedTasksByTaskListId(), (tasks) => {
    return tasks.map((task) => task.id);
  });
};

export const makeGetSubtasksByTaskId = () => {
  return createSelector(
    getAllProjectTemplateElements,
    (state, taskId) => taskId,
    (projectTemplateElements, taskId) => {
      return Object.values(projectTemplateElements).filter(
        (element) => element.class === PROJECT_TEMPLATE_SUBTASK && element.task_id === taskId
      );
    }
  );
};

export const makeGetTaskListIdByTaskId = () => {
  return createSelector(getProjectTemplateElementById, (projectTemplateTask) => {
    return projectTemplateTask.task_list_id;
  });
};

export const makeGetSubtasksByTaskIdSortedByPosition = () => {
  return createSelector(makeGetSubtasksByTaskId(), (subtasks) => {
    return subtasks.sort((a, b) => a.position - b.position);
  });
};

export const makeGetProjectTemplateRecurringTasks = () => {
  return createSelector(
    getAllProjectTemplateElements,
    (state, templateId) => templateId,
    (projectTemplateElements, templateId) => {
      return Object.values(projectTemplateElements).filter(
        (element) => element.class === PROJECT_TEMPLATE_RECURRING_TASK && +element.template_id === templateId
      );
    }
  );
};

export const makeGetFilesByTemplateId = () => {
  return createSelector(
    getAllProjectTemplateElements,
    (state, templateId) => templateId,
    (projectTemplateElements, templateId) => {
      return Object.values(projectTemplateElements).filter(
        (element) => element.class === PROJECT_TEMPLATE_FILE && +element.template_id === templateId
      );
    }
  );
};

export const makeGetSortedFilesByTemplateId = () => {
  return createSelector(makeGetFilesByTemplateId(), (files) => {
    return files.sort((a, b) => b.created_on - a.created_on);
  });
};

export const makeGetNotesByTemplateId = () => {
  return createSelector(
    getAllProjectTemplateElements,
    (state, templateId) => templateId,
    (projectTemplateElements, templateId) => {
      return Object.values(projectTemplateElements).filter(
        (element) => element.class === PROJECT_TEMPLATE_NOTE && +element.template_id === templateId
      );
    }
  );
};

export const makeGetDiscussionsByTemplateId = () => {
  return createSelector(
    getAllProjectTemplateElements,
    (state, templateId) => templateId,
    (projectTemplateElements, templateId) => {
      return Object.values(projectTemplateElements).filter(
        (element) => element.class === PROJECT_TEMPLATE_DISCUSSION && +element.template_id === templateId
      );
    }
  );
};
